import { useState, useEffect, useCallback, useMemo } from "react";
import { useNotificationStore } from "../store/notificationStore";
import {
  ecpNotificationsToMastheadNotifications,
  type MastheadNotificationsResult,
} from "../masthead/mapToMastheadNotifications";
import type { NotificationRecord } from "../store/notificationModalStore";
import { fetchUserNotifications } from "@uhg-ecp/ecp-notifications-sc-subscriber";
import { createLogger } from "../../../utils/logger";
import { getToken } from "../../../config/getToken";
import { withRequestDeadline } from "../../../utils/requestDeadline";

const logger = createLogger("useECPNotifications");
const MAX_NOTIFICATIONS = 25;
const INITIAL_DAYS = 30;
const SELECTED_FIELDS = [
  "requestId",
  "requestEpoch",
  "createdAt",
  "title",
  "message",
  "priority",
  "category",
  "isRead",
  "filters",
];

interface UseECPNotificationsOptions {
  env?: string;
  wsReady?: boolean;
  initialDays?: number;
}

function getNotificationId(notification: NotificationRecord): string {
  return String(
    (notification as Record<string, unknown>).id ??
      notification.requestId ??
      "",
  );
}

function normalizeNotification(notification: unknown): NotificationRecord {
  const n = notification as NotificationRecord;
  return { ...n, id: getNotificationId(n) };
}

export function useECPNotifications({
  env,
  wsReady,
  initialDays = INITIAL_DAYS,
}: UseECPNotificationsOptions): MastheadNotificationsResult {
  const [ecpNotifications, setEcpNotifications] =
    useState<MastheadNotificationsResult>({
      ecpNotifications: [],
      notifications: undefined,
      footerClickCallback: undefined,
      notificationsViewAllLink: "",
      NotificationsOverride: undefined,
      historyHref: "",
    });

  const setInitialNotifications = useNotificationStore(
    (s) => s.setInitialNotifications,
  );

  const onMarkAsRead = useCallback((notificationId: string) => {
    logger.debug("Mark as read:", notificationId);
  }, []);

  const options = useMemo(
    () => ({ env, initialDays, onMarkAsRead }),
    [env, initialDays, onMarkAsRead],
  );

  useEffect(() => {
    if (!env || !wsReady) return;

    let cancelled = false;

    const loadInitialNotifications = async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;

        const rsp = await withRequestDeadline(() =>
          fetchUserNotifications(
            token,
            env,
            { lookbackDays: initialDays, limit: MAX_NOTIFICATIONS },
            SELECTED_FIELDS,
          ),
        );

        if (cancelled) return;

        if (rsp?.success) {
          const items = rsp?.data?.data?.userNotifications?.items ?? [];
          const normalized = (items as unknown[]).map(normalizeNotification);
          setInitialNotifications(normalized);
          setEcpNotifications(
            ecpNotificationsToMastheadNotifications(normalized, options),
          );
        } else {
          logger.error("Failed to fetch notifications:", rsp);
        }
      } catch (error) {
        logger.error("Error fetching notifications:", error);
      }
    };

    loadInitialNotifications();
    return () => {
      cancelled = true;
    };
  }, [env, wsReady, initialDays, options, setInitialNotifications]);

  const notificationList = useNotificationStore((s) => s.notificationList);

  useEffect(() => {
    if (!notificationList?.length) return;

    const normalized = notificationList
      .map(normalizeNotification)
      .slice(0, MAX_NOTIFICATIONS);

    setEcpNotifications(
      ecpNotificationsToMastheadNotifications(normalized, options),
    );
  }, [notificationList, options]);

  return ecpNotifications;
}
